import { Controller } from "stimulus";

import numeral from "numeral";

const centsToDollars = (cents) => {
  return numeral(cents / 100).format("$0,0.00");
};

const WRAP_IMAGE_OPTION_ID = "5";

export default class extends Controller {
  static targets = [
    "canvas",
    "canvasFrameId",
    "canvasMsrp",
    "canvasPrice",
    "canvasProductOutputOptionId",
    "canvasSizeId",
    "canvasAddToCartButton",
    "canvasSubmitButton",
    "print",
    "printMsrp",
    "printPrice",
    "printSizeAndFrameId",
    "printSizeId",
    "printFrameId",
    "frame",
    "frameAddToCartButton",
    "frameFrameId",
    "frameMsrp",
    "framePrice",
    "frameSizeId",
    "outOfStockMessage",
    "wrapImageModal",
    "wrapImageCheckbox",
    "wrapImageAddButton",
    "wrapCheckboxContainer",
    "wrapOption",
    "borderOption",
    "borderPreviewBg",
    "whiteBorderBtn",
    "blackBorderBtn",
  ];

  static values = {
    canvasCornerUrls: Object,
    frameCornerUrls: Object,
    outOfStockSizeAndFrameIds: Array,
    prices: Object,
    imageUrl: String,
  };

  connect() {
    this.update();
  }

  update() {
    if (this.hasFrameSizeIdTarget) {
      this.updateFrames();
    }

    this.updateCanvas();
    this.updatePrint();
  }

  updatePrint() {
    const printSizeAndFrameId = this.printSizeAndFrameIdTarget.value;

    const printPrice = this.pricesValue[printSizeAndFrameId];
    this.printPriceTarget.textContent = centsToDollars(printPrice.price);

    if (printPrice.price != printPrice.msrp) {
      this.printMsrpTarget.textContent = centsToDollars(printPrice.msrp);
    } else {
      this.printMsrpTarget.textContent = "";
    }

    this.printSizeIdTarget.value = printSizeAndFrameId.split("-")[0];
    this.printFrameIdTarget.value = printSizeAndFrameId.split("-")[1];
  }

  updateFrames() {
    const frameSizeId = parseInt(this.frameSizeIdTarget.value);
    const frameFrameId = parseInt(this.frameFrameIdTarget.value);
    const framePrice = this.pricesValue[`${frameSizeId}-${frameFrameId}`];
    this.framePriceTarget.textContent = centsToDollars(framePrice.price);

    if (framePrice.price != framePrice.msrp) {
      this.frameMsrpTarget.textContent = centsToDollars(framePrice.msrp);
    }

    let outOfStock = false;
    this.outOfStockSizeAndFrameIdsValue.forEach(([sizeId, frameId]) => {
      if (sizeId == frameSizeId && frameId == frameFrameId) {
        outOfStock = true;
      }
    });

    if (outOfStock) {
      this.outOfStockMessageTarget.style.display = "block";
      this.frameAddToCartButtonTarget.disabled = true;
    } else {
      this.outOfStockMessageTarget.style.display = "none";
      this.frameAddToCartButtonTarget.disabled = false;
    }

    this.frameTarget.style.backgroundImage = `url("${
      this.frameCornerUrlsValue[this.frameFrameIdTarget.value]
    }")`;
  }

  updateCanvas() {
    const canvasSizeId = this.canvasSizeIdTarget.value;
    const canvasFrameId = this.canvasFrameIdTarget.value;
    const canvasPrice = this.pricesValue[`${canvasSizeId}-${canvasFrameId}`];
    this.canvasPriceTarget.textContent = centsToDollars(canvasPrice.price);

    if (canvasPrice.price != canvasPrice.msrp) {
      this.canvasMsrpTarget.textContent = centsToDollars(canvasPrice.msrp);
    }

    this.canvasTarget.style.backgroundImage = `url("${
      this.canvasCornerUrlsValue[this.canvasProductOutputOptionIdTarget.value]
    }")`;
  }

  handleCanvasAddToCart(event) {
    event.preventDefault();
    const selectedOption = this.canvasProductOutputOptionIdTarget.value;

    if (selectedOption === WRAP_IMAGE_OPTION_ID) {
      this.openWrapImageModal();
    } else {
      this.canvasSubmitButtonTarget.click();
    }
  }

  openWrapImageModal() {
    this.selectedBorderOption = WRAP_IMAGE_OPTION_ID;
    if (this.hasWrapImageCheckboxTarget) {
      this.wrapImageCheckboxTarget.checked = false;
    }
    this.updateModalSelection();
    if (this.hasWrapImageModalTarget) {
      this.wrapImageModalTarget.classList.remove("hidden");
      document.body.style.overflow = "hidden";
      this.boundHandleEscape = this.handleEscape.bind(this);
      document.addEventListener("keydown", this.boundHandleEscape);
    }
  }

  closeWrapImageModal() {
    if (this.hasWrapImageModalTarget) {
      this.wrapImageModalTarget.classList.add("hidden");
      document.body.style.overflow = "";
      if (this.boundHandleEscape) {
        document.removeEventListener("keydown", this.boundHandleEscape);
      }
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeWrapImageModal();
    }
  }

  selectWrapOption(event) {
    event.stopPropagation();
    this.selectedBorderOption = WRAP_IMAGE_OPTION_ID;
    this.updateModalSelection();
  }

  selectBorderOption(event) {
    event.stopPropagation();
    if (
      !this.selectedBorderOption ||
      this.selectedBorderOption === WRAP_IMAGE_OPTION_ID
    ) {
      this.selectedBorderOption = "6";
    }
    this.updateModalSelection();
  }

  selectWhiteBorder(event) {
    event.stopPropagation();
    this.selectedBorderOption = "6";
    this.updateModalSelection();
  }

  selectBlackBorder(event) {
    event.stopPropagation();
    this.selectedBorderOption = "7";
    this.updateModalSelection();
  }

  updateModalSelection() {
    const isWrapImage = this.selectedBorderOption === WRAP_IMAGE_OPTION_ID;

    if (this.hasWrapOptionTarget) {
      this.wrapOptionTarget.classList.toggle("border-va-blue", isWrapImage);
      this.wrapOptionTarget.classList.toggle(
        "border-neutral-200",
        !isWrapImage,
      );
    }

    if (this.hasBorderOptionTarget) {
      this.borderOptionTarget.classList.toggle("border-va-blue", !isWrapImage);
      this.borderOptionTarget.classList.toggle(
        "border-neutral-200",
        isWrapImage,
      );
    }

    if (this.hasBorderPreviewBgTarget) {
      this.borderPreviewBgTarget.classList.remove(
        "bg-gray-200",
        "bg-white",
        "bg-black",
      );
      if (this.selectedBorderOption === "6") {
        this.borderPreviewBgTarget.classList.add("bg-white");
      } else if (this.selectedBorderOption === "7") {
        this.borderPreviewBgTarget.classList.add("bg-black");
      } else {
        this.borderPreviewBgTarget.classList.add("bg-gray-200");
      }
    }

    if (this.hasWhiteBorderBtnTarget) {
      this.whiteBorderBtnTarget.classList.toggle(
        "border-va-blue",
        this.selectedBorderOption === "6",
      );
      this.whiteBorderBtnTarget.classList.toggle(
        "border-neutral-300",
        this.selectedBorderOption !== "6",
      );
    }

    if (this.hasBlackBorderBtnTarget) {
      this.blackBorderBtnTarget.classList.toggle(
        "border-va-blue",
        this.selectedBorderOption === "7",
      );
      this.blackBorderBtnTarget.classList.toggle(
        "border-neutral-300",
        this.selectedBorderOption !== "7",
      );
    }

    if (this.hasWrapCheckboxContainerTarget) {
      this.wrapCheckboxContainerTarget.classList.toggle("hidden", !isWrapImage);
    }

    if (this.hasWrapImageAddButtonTarget) {
      if (isWrapImage) {
        this.wrapImageAddButtonTarget.disabled =
          !this.wrapImageCheckboxTarget?.checked;
      } else {
        this.wrapImageAddButtonTarget.disabled = false;
      }
    }
  }

  toggleWrapImageConfirm() {
    if (this.hasWrapImageAddButtonTarget && this.hasWrapImageCheckboxTarget) {
      this.wrapImageAddButtonTarget.disabled =
        !this.wrapImageCheckboxTarget.checked;
    }
  }

  confirmWrapImageAddToCart() {
    this.canvasProductOutputOptionIdTarget.value = this.selectedBorderOption;
    this.updateCanvas();
    this.closeWrapImageModal();
    this.canvasSubmitButtonTarget.click();
  }
}
